import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { BoardRenderer } from '../render/boardRenderer.ts';
import type { TileRef } from '../engine/modes.ts';
import type { Board as BoardModel } from '../engine/settle.ts';

export interface BoardHandle {
  /** Animate a committed move. The logical state has already changed. */
  playMove(removed: readonly TileRef[]): Promise<void>;
  /** Animate a shuffle: the tiles turn over in place and come back with new letters. */
  playShuffle(board: BoardModel): Promise<void>;
  /** Snap to a board with no animation - load, undo, restart. */
  setBoard(board: BoardModel): void;
}

interface BoardProps {
  board: BoardModel;
  w: number;
  h: number;
  selection: readonly TileRef[];
  disabled: boolean;
  /** Draw the selection as a path of arrows. False where letters need not touch. */
  showPath: boolean;
  onTileDown(x: number, y: number): void;
  onTileEnter(x: number, y: number): void;
  onGestureEnd(): void;
  onGestureCancel(): void;
  /** The accessible grid commits a tile per activation rather than by dragging. */
  onTileActivate(x: number, y: number): void;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const Board = forwardRef<BoardHandle, BoardProps>(function Board(props, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<BoardRenderer | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // The callbacks are read through a ref so the renderer, which is created once, always
  // calls the CURRENT handlers. Recreating the renderer whenever a prop changed would
  // drop the WebGL context on every React render.
  const handlers = useRef(props);

  handlers.current = props;

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const renderer = new BoardRenderer(
      canvas,
      props.w,
      props.h,
      {
        onTileDown: (x, y) => {
          if (!handlers.current.disabled) handlers.current.onTileDown(x, y);
        },
        onTileEnter: (x, y) => {
          if (!handlers.current.disabled) handlers.current.onTileEnter(x, y);
        },
        onPointerUp: () => handlers.current.onGestureEnd(),
        onPointerCancel: () => handlers.current.onGestureCancel(),
      },
      prefersReducedMotion(),
    );

    const started = renderer.start();

    if (!started.ok) {
      // A useful message rather than a blank page. The rules still work; it is only the
      // board that cannot be drawn, so the accessible grid below remains playable.
      setFailure(started.reason);
      return;
    }

    rendererRef.current = renderer;
    renderer.setShowPath(handlers.current.showPath);
    renderer.setBoard(handlers.current.board);
    renderer.setSelection(handlers.current.selection);

    const observer = new ResizeObserver(() => renderer.resize());

    observer.observe(canvas);

    // Orientation changes resize the canvas without always firing a resize on the element
    // in time, so this is belt and braces.
    const onOrientation = (): void => renderer.resize();

    window.addEventListener('orientationchange', onOrientation);

    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', onOrientation);
      renderer.dispose();
      rendererRef.current = null;
    };
    // Created once for a given board size. Contents are pushed imperatively.
  }, [props.w, props.h]);

  useEffect(() => {
    rendererRef.current?.setShowPath(props.showPath);
    rendererRef.current?.setSelection(props.selection);
  }, [props.selection, props.showPath]);

  useImperativeHandle(
    ref,
    () => ({
      async playMove(removed) {
        await rendererRef.current?.playMove(removed);
      },
      async playShuffle(board) {
        await rendererRef.current?.playShuffle(board);
      },
      setBoard(board) {
        rendererRef.current?.setBoard(board);
        rendererRef.current?.setSelection([]);
      },
    }),
    [],
  );

  const selectedKeys = new Set(props.selection.map((tile) => `${tile.x},${tile.y}`));

  return (
    <div className="board">
      <canvas ref={canvasRef} aria-hidden="true" />

      {failure ? (
        <div className="board__fallback" role="status">
          The board needs WebGL, which this browser has turned off or cannot provide
          {failure ? ` (${failure})` : ''}. You can still play with the letter grid below.
        </div>
      ) : null}

      {/* The same board, operable from a keyboard or a screen reader, driven by the same
          engine. Visually hidden until focused so it does not duplicate the canvas. */}
      <div className="board__a11y">
        <h2 className="visually-hidden">Letter grid</h2>
        <div
          className="a11y-grid"
          role="grid"
          aria-label={`${props.w} by ${props.h} letter grid`}
          style={{ gridTemplateColumns: `repeat(${props.w}, minmax(32px, 1fr))` }}
        >
          {Array.from({ length: props.h }, (_, row) => {
            // Row 0 of the grid is the TOP of the board, which is the highest y.
            const y = props.h - 1 - row;

            return Array.from({ length: props.w }, (_, x) => {
              const letter = props.board[y * props.w + x];
              const key = `${x},${y}`;

              if (letter == null) {
                return (
                  <div
                    key={key}
                    role="gridcell"
                    className="a11y-cell"
                    aria-label={`Row ${row + 1}, column ${x + 1}, empty`}
                  />
                );
              }

              return (
                <button
                  key={key}
                  type="button"
                  role="gridcell"
                  className="a11y-cell"
                  aria-pressed={selectedKeys.has(key)}
                  aria-label={`${letter.toUpperCase()}, row ${row + 1}, column ${x + 1}`}
                  disabled={props.disabled}
                  onClick={() => props.onTileActivate(x, y)}
                >
                  {letter.toUpperCase()}
                </button>
              );
            });
          })}
        </div>
      </div>
    </div>
  );
});
